import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Shield, Heart, Users, Award } from 'lucide-react';
import { ContactCTA } from '../components/ContactCTA';
export function AboutPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const values = [{
    icon: Shield,
    title: 'Integrity',
    desc: "We provide honest assessments and transparent pricing. If a repair will do, we won't sell you a replacement."
  }, {
    icon: Award,
    title: 'Quality',
    desc: 'We use only premium materials from trusted manufacturers and follow strict installation standards on every project.'
  }, {
    icon: Heart,
    title: 'Community',
    desc: 'We live and work in the same neighborhoods we serve. Your community is our community.'
  }, {
    icon: Users,
    title: 'Reliability',
    desc: 'We show up on time, communicate clearly, and stand behind our work with comprehensive warranties.'
  }];
  const timeline = [{
    year: '2009',
    title: 'Founded',
    desc: 'Dominic Moceri establishes Moceri Residential Services in Harrison Township, MI.'
  }, {
    year: '2012',
    title: 'Expanded to St. Clair County',
    desc: 'Growing reputation leads to projects across St. Clair County communities.'
  }, {
    year: '2015',
    title: '1,000th Project',
    desc: 'Milestone reached — 1,000 homes improved across Southeast Michigan.'
  }, {
    year: '2018',
    title: 'Commercial Division',
    desc: 'Launched commercial roofing services including TPO, EPDM, and silicone coatings.'
  }, {
    year: '2024',
    title: 'Serving 3 Counties',
    desc: 'Now proudly serving Macomb, St. Clair, and Oakland counties with a full fleet.'
  }];
  return <div className="w-full bg-white" data-id="element-461">
      {/* Hero */}
      <section className="relative min-h-[40vh] flex items-center pt-20 pb-16 overflow-hidden" data-id="element-462">
        <div className="absolute inset-0 z-0" data-id="element-463">
          <img src="https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=1920&q=80" alt="" className="absolute inset-0 w-full h-full object-cover" data-id="element-464" />
          <div className="absolute inset-0 bg-brand-blue/15 z-[1]" data-id="element-465"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-black/30 z-[2]" data-id="element-466"></div>
        </div>
        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full" data-id="element-467">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-468">
            <h1 className="font-heading font-black text-4xl md:text-5xl lg:text-6xl text-white leading-tight mb-4" data-id="element-469">
              About Moceri{' '}
              <span className="text-brand-blue" data-id="element-470">Residential Services</span>
            </h1>
            <p className="font-body text-xl text-gray-300 max-w-2xl" data-id="element-471">
              Built on trust, driven by craftsmanship — serving Southeast
              Michigan since 2009.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Dominic's Story */}
      <section className="py-20" data-id="element-472">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-473">
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20" data-id="element-474">
            <motion.div initial={{
            opacity: 0,
            x: -40
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="w-full lg:w-5/12" data-id="element-475">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl" data-id="element-476">
                <div className="brand-overlay" data-id="element-477">
                  <img src="https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=800&q=80" alt="Dominic Moceri" className="w-full h-[500px] object-cover" data-id="element-478" />
                </div>
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            x: 40
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="w-full lg:w-7/12" data-id="element-479">
              <h2 className="font-heading font-bold text-3xl md:text-4xl text-brand-gray-dark mb-4" data-id="element-480">
                The Dominic Moceri Story
              </h2>
              <div className="w-20 h-1 bg-brand-blue mb-8 rounded-full" data-id="element-481"></div>
              <div className="font-body text-brand-gray text-lg leading-relaxed space-y-6" data-id="element-482">
                <p data-id="element-483">
                  In 2009, Dominic Moceri made a decision that would shape his
                  life and the homes of thousands of Michigan families. With a
                  builder's license in hand and a deep commitment to honest
                  work, he founded Moceri Residential Services right here in
                  Harrison Township.
                </p>
                <p data-id="element-484">
                  What started as a one-man operation quickly grew through
                  word-of-mouth referrals. Neighbors told neighbors. Families
                  across Macomb County, then St. Clair County, and eventually
                  Oakland County began calling on Dominic and his growing team
                  for their roofing, siding, gutter, and exterior needs.
                </p>
                <p data-id="element-485">
                  Today, Moceri Residential Services operates a full fleet of
                  branded vehicles and a team of skilled professionals. But the
                  core values haven't changed: show up on time, do the job
                  right, use the best materials, and treat every home like it's
                  your own. That's the Moceri promise.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 bg-gray-50" data-id="element-486">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-487">
          <div className="text-center mb-16" data-id="element-488">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-brand-gray-dark mb-4" data-id="element-489">
              Our Journey
            </h2>
            <div className="w-20 h-1 bg-brand-blue mx-auto rounded-full" data-id="element-490"></div>
          </div>
          <div className="relative" data-id="element-491">
            <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-0.5 bg-brand-blue/20 -translate-x-1/2" data-id="element-492"></div>
            <div className="space-y-12" data-id="element-493">
              {timeline.map((item, index) => <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.1
            }} className={`relative flex items-start gap-6 md:gap-12 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`} data-id="element-494">
                  <div className="hidden md:block w-1/2" data-id="element-495"></div>
                  <div className="absolute left-8 md:left-1/2 w-4 h-4 bg-brand-blue rounded-full -translate-x-1/2 mt-2 border-4 border-white shadow-md z-10" data-id="element-496"></div>
                  <div className="ml-16 md:ml-0 md:w-1/2 bg-white p-6 rounded-xl shadow-sm border border-gray-100" data-id="element-497">
                    <span className="font-heading font-bold text-brand-blue text-2xl" data-id="element-498">
                      {item.year}
                    </span>
                    <h3 className="font-heading font-bold text-xl text-brand-gray-dark mt-1 mb-2" data-id="element-499">
                      {item.title}
                    </h3>
                    <p className="font-body text-brand-gray" data-id="element-500">{item.desc}</p>
                  </div>
                </motion.div>)}
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-white" data-id="element-501">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-502">
          <div className="text-center mb-16" data-id="element-503">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-brand-gray-dark mb-4" data-id="element-504">
              Our Core Values
            </h2>
            <div className="w-20 h-1 bg-brand-blue mx-auto rounded-full" data-id="element-505"></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-506">
            {values.map((value, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className="text-center p-8 rounded-xl bg-gray-50 border border-gray-100" data-id="element-507">
                <div className="w-16 h-16 bg-brand-blue/10 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-508">
                  <value.icon size={32} className="text-brand-blue" data-id="element-509" />
                </div>
                <h3 className="font-heading font-bold text-xl text-brand-gray-dark mb-3" data-id="element-510">
                  {value.title}
                </h3>
                <p className="font-body text-brand-gray text-sm" data-id="element-511">
                  {value.desc}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      <ContactCTA data-id="element-512" />
    </div>;
}